import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SectionHeader from '../components/SectionHeader';
import NewsList from '../components/NewsList';
import Pagination from '../components/Pagination';
import { NewsResponse, getNews } from '../utils/newsApi';
import { usePageMeta } from '../utils/usePageMeta';

const NewsPage: React.FC = () => {
  const params = useParams<{ page?: string }>();
  const page = params.page === undefined ? 1 : Number(params.page);
  const validPage = Number.isInteger(page) && page >= 1;

  const [response, setResponse] = useState<NewsResponse | null>(null);
  const [error, setError] = useState(false);

  usePageMeta(
    page > 1 ? `News - Page ${page} | The Creamery` : 'News | The Creamery',
    'Latest news and updates from The Creamery sports bar, restaurant and snooker hall in Falkirk.'
  );

  useEffect(() => {
    if (!validPage) return;
    const controller = new AbortController();
    setResponse(null);
    setError(false);
    getNews(page, 9, controller.signal)
      .then(setResponse)
      .catch((err) => {
        if (err.name !== 'AbortError') setError(true);
      });
    return () => controller.abort();
  }, [page, validPage]);

  const outOfRange = response !== null && page > Math.max(response.meta.last_page, 1);

  return (
    <div className="pt-20 bg-primary-black min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <SectionHeader
          eyebrow="WHAT'S ON"
          title="Latest News"
          subtitle="The latest news and updates from The Creamery."
          darkMode={true}
        />

        {(!validPage || outOfRange) && (
          <p className="text-center text-gray-300">
            That page of news doesn't exist. <Link to="/news" className="text-primary-gold underline">Back to news</Link>
          </p>
        )}
        {validPage && error && (
          <p role="alert" className="text-center text-gray-300">
            We couldn't load the news just now. Please try again later.
          </p>
        )}
        {validPage && !error && response === null && (
          <p className="text-center text-gray-400" aria-live="polite">Loading news...</p>
        )}
        {response && !outOfRange && response.data.length === 0 && (
          <p className="text-center text-gray-300">No news yet. Check back soon!</p>
        )}
        {response && !outOfRange && response.data.length > 0 && (
          <>
            <NewsList items={response.data} />
            <Pagination page={page} totalPages={response.meta.last_page} />
          </>
        )}
      </div>
    </div>
  );
};

export default NewsPage;
